import type { Row } from "@rocicorp/zero";
import type { useZero } from "@rocicorp/zero/react";
import { keyframes, styled } from "goober";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { ChiliPiperLead } from "../../../shared/chiliPiperShell";
import { splitLeadName } from "../../../shared/leadName";
import { sharedMutators } from "../../../shared/mutators";
import type { DemoRequestStage } from "../../../shared/mutators/recordDemoRequest";
import { config } from "../../environment";
import { demoRequestAttribution } from "../../lib/demoRequestAttribution";
import { zIndexes } from "../../lib/zIndexes";
import { Icon } from "../Icon";
import { Modal } from "../Modal";
import { PressButton } from "../PressButton";
import { ChiliPiperFrame } from "./ChiliPiperFrame";

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled("span")`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid var(--border-alpha);
  border-top-color: var(--text-secondary);
  animation: ${spin} 0.7s linear infinite;
`;

const LoadingBody = styled("div")`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 28px 32px;
  font-size: 14px;
  color: var(--text-primary);
`;

const FallbackBody = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 28px 32px;
  font-size: 14px;
  color: var(--text-primary);
`;

const FallbackLink = styled("a")`
  align-self: flex-start;
  color: var(--orange-1000);
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const BookingOverlay = styled("div")`
  position: fixed;
  inset: 0;
  z-index: ${zIndexes.demoBooking};
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--modal-backdrop);
  transition: opacity 150ms ease;

  &[data-visible="false"] {
    opacity: 0;
    pointer-events: none;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const BookingCard = styled("div")`
  position: relative;
  background: var(--surface-primary);
  border-radius: 16px;
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
  padding: 44px 16px 16px;
  max-width: 90vw;
  max-height: 90vh;
  overflow: auto;
`;

const CloseButton = styled(PressButton)`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  color: var(--text-secondary);

  &:hover {
    background: var(--alpha4);
  }
`;

const CalendarHost = styled("div")`
  width: 420px;
  max-width: 86vw;
  height: 560px;
  max-height: 80vh;
`;

export function leadFromUser(user: Row["user"]): ChiliPiperLead {
  const email = user.email ?? "";
  const { firstName, lastName } = splitLeadName(user.name ?? "", email);
  return {
    FirstName: firstName,
    LastName: lastName || undefined,
    Email: email,
  };
}

export const AVAILABILITY_TIMEOUT_MS = 15_000;

export function useGetDemo({
  user,
  zero,
}: {
  user: Row["user"];
  zero: ReturnType<typeof useZero>;
}) {
  const enabled = config.chiliPiperEnabled;
  const fallbackUrl = config.chiliPiperFallbackUrl;

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isBookingVisible, setIsBookingVisible] = useState(false);
  const [isFallbackVisible, setIsFallbackVisible] = useState(false);

  // A reported request stays owed an outcome until the booking is confirmed or
  // the flow ends, at which point exactly one of success/cancel is sent.
  const outcomeOwed = useRef(false);

  const lead = useMemo(() => leadFromUser(user), [user]);

  const reset = useCallback(() => {
    setIsOpen(false);
    setIsLoading(false);
    setIsBookingVisible(false);
    setIsFallbackVisible(false);
  }, []);

  const record = useCallback(
    (stage: DemoRequestStage) => {
      zero.mutate(
        sharedMutators.recordDemoRequest({
          stage,
          marketingAttribution: demoRequestAttribution(),
        })
      );
    },
    [zero]
  );

  // Claiming the outcome before reporting it keeps this idempotent: a flow that
  // ends twice, or a booking ChiliPiper confirms twice, still reaches HQ once.
  const recordOutcome = useCallback(
    (stage: Exclude<DemoRequestStage, "submitted">) => {
      if (!outcomeOwed.current) return;
      outcomeOwed.current = false;
      record(stage);
    },
    [record]
  );

  const recordCancelled = useCallback(
    () => recordOutcome("cancel"),
    [recordOutcome]
  );

  const openFallback = useCallback(() => {
    if (fallbackUrl) window.open(fallbackUrl, "_blank", "noopener");
  }, [fallbackUrl]);

  // Reached from postMessage handlers and the timeout, never a user gesture, so
  // window.open would be popup-blocked — surface the fallback modal (a link when
  // one is configured, otherwise a message) so the flow always ends visibly.
  const showFallback = useCallback(() => {
    recordCancelled();
    setIsOpen(false);
    setIsLoading(false);
    setIsBookingVisible(false);
    setIsFallbackVisible(true);
  }, [recordCancelled]);

  const closeDemo = useCallback(() => {
    recordCancelled();
    reset();
  }, [recordCancelled, reset]);

  const openDemo = useCallback(() => {
    // HQ counts the request as soon as the flow opens, so a lead reaches the
    // pipeline even when the user abandons the calendar or never sees one.
    outcomeOwed.current = true;
    record("submitted");
    if (!enabled) {
      openFallback();
      recordCancelled();
      return;
    }
    setIsOpen(true);
    setIsLoading(true);
    setIsBookingVisible(false);
  }, [enabled, openFallback, record, recordCancelled]);

  // ChiliPiper keeps its own confirmation on screen, so the booking stays open
  // until the user closes it — by which point no cancellation is owed.
  const onBooked = useCallback(() => recordOutcome("success"), [recordOutcome]);

  const onAvailability = useCallback(
    (hasAvailability: boolean) => {
      if (!hasAvailability) {
        showFallback();
        return;
      }
      setIsLoading(false);
      setIsBookingVisible(true);
    },
    [showFallback]
  );

  const onError = useCallback(() => {
    showFallback();
  }, [showFallback]);

  // ChiliPiper can load but never emit an availability event (e.g. an ad blocker
  // nulls the widget after the script loads), which would leave the spinner up
  // forever with no path to the fallback.
  useEffect(() => {
    if (!isLoading) return;
    const timer = setTimeout(() => {
      showFallback();
    }, AVAILABILITY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isLoading, showFallback]);

  useEffect(() => {
    if (!isBookingVisible) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeDemo();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isBookingVisible, closeDemo]);

  // Leaving mid-booking abandons it, whether or not a slot was picked: once the
  // page or the booking frame is gone, a booking ChiliPiper never confirmed can
  // no longer complete. Best-effort on unload — the mutation is queued as the
  // page goes away, so it may not reach the server until the next visit.
  useEffect(() => {
    window.addEventListener("beforeunload", recordCancelled);
    return () => window.removeEventListener("beforeunload", recordCancelled);
  }, [recordCancelled]);

  // An in-app navigation fires no beforeunload, and every page renders its own
  // Backdrop, so leaving the page tears this hook down mid-booking instead.
  // Read through a ref so only a real unmount reports, never a re-render that
  // happens to change the callback's identity.
  const recordCancelledRef = useRef(recordCancelled);
  useEffect(() => {
    recordCancelledRef.current = recordCancelled;
  }, [recordCancelled]);
  useEffect(() => () => recordCancelledRef.current(), []);

  const modals = (
    <>
      <Modal open={isLoading} onClose={closeDemo}>
        <LoadingBody>
          <Spinner />
          <span>Finding available slots…</span>
        </LoadingBody>
      </Modal>
      <Modal open={isFallbackVisible} onClose={closeDemo}>
        <FallbackBody>
          {fallbackUrl ? (
            <>
              <span>Let’s get your demo booked.</span>
              <FallbackLink
                href={fallbackUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeDemo}
              >
                Book a demo
              </FallbackLink>
            </>
          ) : (
            <span>
              We couldn’t start your demo booking just now. Please try again
              later.
            </span>
          )}
        </FallbackBody>
      </Modal>
      {isOpen &&
        createPortal(
          <BookingOverlay
            data-visible={isBookingVisible ? "true" : "false"}
            onClick={(event) => {
              if (event.target === event.currentTarget) closeDemo();
            }}
          >
            <BookingCard>
              <CloseButton onPress={closeDemo} aria-label="Close booking">
                <Icon name="x" size={16} />
              </CloseButton>
              <CalendarHost>
                <ChiliPiperFrame
                  lead={lead}
                  onAvailability={onAvailability}
                  onBooked={onBooked}
                  onClosed={closeDemo}
                  onError={onError}
                />
              </CalendarHost>
            </BookingCard>
          </BookingOverlay>,
          document.body
        )}
    </>
  );

  return { available: enabled, openDemo, modals };
}
